import React, { useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useAgentWithSubagents } from '../../hooks/useAgentWithSubagents';
import { useSubscriptionForRenewalDate } from '../../hooks/useSubscriptionForRenewalDate';
import AgentOutputActions from '../../components/Agents/AgentOutputActions';
import SecurityViolationModal from '../../components/Agents/SecurityViolationModal';
import { CreditExhaustedModal } from '../../components/Agents/CreditExhaustedModal';
import { CreditUpgradeNudgeModal } from '../../components/Agents/CreditUpgradeNudgeModal';
import { validateFields } from '../../utils/securityValidator';
import { formatElapsedTime } from '../../utils/timeUtils';
import { 
  AgentPageLayout, 
  AgentFormSection, 
  AgentButton, 
  AgentError,
  AgentDisclaimer
} from '../../components/Agents/AgentPageLayout';

// --- UI Components ---

const BiblicalReferenceInput: React.FC<{ value: string; onChange: (value: string) => void; isLoading: boolean; }> = ({ value, onChange, isLoading }) => (
    <div className="space-y-2">
      <label htmlFor="biblicalReferenceInput" className="block text-sm font-medium text-foreground">Referência Bíblica:</label>
      <input 
        id="biblicalReferenceInput" 
        type="text" 
        value={value} 
        onChange={(e) => onChange(e.target.value)} 
        placeholder="Ex. João 3:16; Amós 1.1-2" 
        aria-label="Referência Bíblica" 
        disabled={isLoading}
        className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
      />
    </div>
);

const AnalysisLevelSelect: React.FC<{ value: string; onChange: (value: string) => void; isLoading: boolean; }> = ({ value, onChange, isLoading }) => (
    <div className="space-y-2">
      <label htmlFor="analysisLevelSelect" className="block text-sm font-medium text-foreground">Nível de Análise:</label>
      <select 
        id="analysisLevelSelect" 
        value={value} 
        onChange={(e) => onChange(e.target.value)} 
        disabled={isLoading} 
        aria-label="Nível de Análise"
        className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <option value="Essencial">Essencial</option>
        <option value="Expandida">Expandida</option>
        <option value="Profunda">Profunda</option>
      </select>
    </div>
);

interface WordDetail { label: string; value: string; isLink?: boolean; isMarkdown?: boolean; }
interface ParsedWord { termName: string; details: WordDetail[]; }
type SectionElement = { type: 'H1_MARKDOWN'; titleLine: string; contentLines: string[] } | { type: 'WORD_TABLE'; wordData: ParsedWord };
const LinkRenderer = ({ node, ...props }: any) => <a {...props} target="_blank" rel="noopener noreferrer" />;
const parseWordBlockContent = (termName: string, blockLines: string[]): ParsedWord => {
  const wordData: ParsedWord = { termName, details: [] };
  let currentDetailLabel: string | null = null, currentDetailContent: string[] = [], inAnalysisMarkdown = false;
  const finalizePreviousDetail = () => {
    if (currentDetailLabel && currentDetailContent.length > 0) {
      const value = currentDetailContent.join('\n').trim();
      wordData.details.push({ label: currentDetailLabel, value: value, isLink: currentDetailLabel.toLowerCase().includes('link para consulta no biblehub'), isMarkdown: currentDetailLabel.toLowerCase().startsWith('análise lexical e semântica do termo') });
    }
    currentDetailLabel = null; currentDetailContent = []; inAnalysisMarkdown = false;
  };
  for (const line of blockLines) {
    if (line.match(/^###\s+/)) { finalizePreviousDetail(); continue; }
    const detailMatch = line.match(/^\s*-\s+\**(.*?)\**:\s*(.*)/);
    if (detailMatch) {
      finalizePreviousDetail(); 
      const [_, label, valuePart] = detailMatch;
      currentDetailLabel = label.trim();
      currentDetailContent.push(valuePart.trim());
      if (label.toLowerCase().startsWith('análise lexical e semântica do termo')) inAnalysisMarkdown = true; else finalizePreviousDetail();
    } else if (inAnalysisMarkdown && currentDetailLabel) { currentDetailContent.push(line); }
  }
  finalizePreviousDetail();
  return wordData;
};
const parseGeminiMarkdown = (markdown: string): SectionElement[] => {
  const elements: SectionElement[] = [];
  const lines = markdown.split('\n');
  let currentH1TitleLine: string | null = null, currentH1ContentLines: string[] = [], currentWordBlockLines: string[] = [], currentWordTermName: string | null = null;
  const flushH1Section = () => { if (currentH1TitleLine) elements.push({ type: 'H1_MARKDOWN', titleLine: currentH1TitleLine, contentLines: currentH1ContentLines }); currentH1TitleLine = null; currentH1ContentLines = []; };
  const flushWordBlock = () => { if (currentWordTermName && currentWordBlockLines.length > 0) elements.push({ type: 'WORD_TABLE', wordData: parseWordBlockContent(currentWordTermName, currentWordBlockLines) }); currentWordTermName = null; currentWordBlockLines = []; };
  for (const line of lines) {
    const h1Match = line.match(/^#\s+(.*)/), h2WordMatch = line.match(/^##\s+3\.\d+\s+\[(.*?)\]/); // Adjusted for re-numbering
    if (h1Match) { flushWordBlock(); flushH1Section(); currentH1TitleLine = line; } 
    else if (h2WordMatch && currentH1TitleLine?.includes("3. Análise dos Termos")) { flushWordBlock(); currentWordTermName = h2WordMatch[1]; } 
    else if (currentWordTermName) { currentWordBlockLines.push(line); } 
    else if (currentH1TitleLine) { currentH1ContentLines.push(line); }
  }
  flushWordBlock(); flushH1Section();
  return elements;
};
const AnalysisDisplay: React.FC<{ markdownContent: string; }> = ({ markdownContent }) => {
  if (!markdownContent || markdownContent.trim().length === 0) {
    return <div className="text-muted-foreground p-4">Conteúdo não disponível</div>;
  }
  const sections = parseGeminiMarkdown(markdownContent);
  const hasWordTables = sections.some(s => s.type === 'WORD_TABLE');
  return (
    <div className="prose prose-invert max-w-none text-foreground">
      {sections.map((section, index) => {
        if (section.type === 'H1_MARKDOWN') {
          const fullMarkdown = `${section.titleLine}\n${section.contentLines.join('\n')}`;
          if (section.titleLine.includes("3. Análise dos Termos") && section.contentLines.join('\n').trim() === '' && hasWordTables) {
            return <div key={`h1-title-${index}`} className="mb-0"><ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: LinkRenderer }}>{section.titleLine}</ReactMarkdown></div>;
          }
          return <div key={`h1-section-${index}`} className="mb-6"><ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: LinkRenderer }}>{fullMarkdown}</ReactMarkdown></div>;
        } else if (section.type === 'WORD_TABLE') {
          return (
            <div key={`word-${section.wordData.termName}-${index}`} className="mb-6 overflow-x-auto">
              <table className="w-full text-left border-collapse border border-border rounded-lg overflow-hidden">
                <thead>
                  <tr>
                    <th scope="col" className="bg-card px-4 py-3 text-xl font-bold text-foreground border-b border-border">
                      {section.wordData.termName}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {section.wordData.details.map((detail, detailIndex) => (
                    <tr key={detailIndex} className="border-b border-border last:border-b-0 hover:bg-muted/30">
                      <td className="px-4 py-3 align-top">
                        <strong className="block mb-1 text-foreground font-semibold">{detail.label}:</strong>
                        {detail.isLink ? (
                          <a href={detail.value} target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">
                            {detail.value}
                          </a>
                        ) : detail.isMarkdown ? (
                          <div className="prose prose-invert max-w-none text-foreground">
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: LinkRenderer }}>
                              {detail.value}
                            </ReactMarkdown>
                          </div>
                        ) : (
                          <span className="whitespace-pre-wrap text-foreground">{detail.value}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
};

// --- Main App Component ---

const WordAnalyzer: React.FC = () => {
  const [biblicalReference, setBiblicalReference] = useState<string>('');
  const [analysisLevel, setAnalysisLevel] = useState<string>('Essencial');
  
  const [showSecurityModal, setShowSecurityModal] = useState<boolean>(false);
  const {
    isLoading,
    error,
    result,
    steps,
    hasStarted,
    elapsedTime,
    execute,
    reset,
    creditsExhausted,
    creditsInfo,
    setCreditsExhausted,
    canRetry,
    onRetry,
    creditUpgradeNudgeOpen,
    creditUpgradeNudgeUsagePercent,
    dismissCreditUpgradeNudge,
  } = useAgentWithSubagents('analisar-palavras');
  const renewalDate = useSubscriptionForRenewalDate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!biblicalReference.trim()) {
      return;
    }

    // Validar segurança antes de enviar
    if (validateFields(biblicalReference)) {
      setShowSecurityModal(true);
      return;
    }

    const parameters: Record<string, any> = {
      biblical_reference: biblicalReference,
      analysis_level: analysisLevel,
    };

    await execute(parameters);
  };

  const handleReset = useCallback(() => {
    setBiblicalReference('');
    setAnalysisLevel('Essencial');
    reset();
  }, [reset]);

  return (
    <AgentPageLayout
      icon={<MagnifyingGlassIcon className="w-6 h-6 text-secondary" />}
      title="Analisar Palavras"
      subtitle="Analise termos bíblicos revelando sentidos originais, contexto teológico e nuances de significado."
    >
      <AgentFormSection>
        <form onSubmit={handleSubmit} className="space-y-6">
          <BiblicalReferenceInput value={biblicalReference} onChange={setBiblicalReference} isLoading={isLoading} />
          <AnalysisLevelSelect value={analysisLevel} onChange={setAnalysisLevel} isLoading={isLoading} />
          <div className="flex justify-end">
            <AgentButton type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Analisando...</span>
                </>
              ) : (
                <>
                  <MagnifyingGlassIcon className="w-5 h-5" />
                  <span>Analisar Palavras</span>
                </>
              )}
            </AgentButton>
          </div>
        </form>

        {error && <AgentError message={error} onRetry={canRetry ? onRetry : undefined} />}
      </AgentFormSection>
      
      {hasStarted && (
        <section className="mt-8">
          <div className="bg-card border border-border rounded-lg p-6">
            {isLoading && (
              <div className="flex flex-col items-center justify-center gap-4 py-8">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">😇</span>
                  <h3 className="text-lg font-semibold text-foreground">
                    Enquanto trabalhamos, eleve seu pensamento e descanse na presença de Deus.
                  </h3>
                </div>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
                {steps.length > 0 && steps[0]?.title && (
                  <p className="text-sm text-muted-foreground">{steps[0].title}</p>
                )}
                {elapsedTime > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Tempo de execução: {formatElapsedTime(elapsedTime)}
                  </p>
                )}
              </div>
            )}
            
            {result && result.trim().length > 0 && (
              <>
                <div className="mt-6">
                  <AnalysisDisplay markdownContent={result} />
                </div>
                <AgentDisclaimer />
                <div className="mt-6 flex items-center justify-end gap-3">
                  <AgentOutputActions
                    content={result}
                    agentName="Analisar Palavras"
                    agentCode="analisar-palavras"
                    reference={biblicalReference}
                  />
                  <AgentButton
                    type="button"
                    onClick={handleReset}
                    variant="secondary"
                    aria-label="Reiniciar Análise"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                    </svg>
                    <span>Reiniciar</span>
                  </AgentButton>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      <SecurityViolationModal
        open={showSecurityModal}
        onClose={() => setShowSecurityModal(false)}
        onRestart={handleReset}
      />
      <CreditUpgradeNudgeModal
        isOpen={creditUpgradeNudgeOpen}
        onClose={dismissCreditUpgradeNudge}
        usagePercent={creditUpgradeNudgeUsagePercent}
      />
      {creditsExhausted && (
        <CreditExhaustedModal
          isOpen={creditsExhausted}
          onClose={() => setCreditsExhausted(false)}
          renewalDate={renewalDate}
          creditsInfo={creditsInfo || undefined}
        />
      )}
    </AgentPageLayout>
  );
};

export default WordAnalyzer;
